import React, { useCallback, useEffect, useId, useMemo, useState } from 'react'
import { InputAdornment, Tooltip } from '@mui/material'

import { apiZodios } from '~api/apiZodios'
import { Icon, IconButton, TextField, Typography } from '~components/internal'
import { type PasswordPolicy } from '~types'

import './SetPasswordInput.scss'

interface Requirement {
  label: string
  test: (value: string) => boolean
}

const FALLBACK_POLICY: PasswordPolicy = {
  min_length: 8,
  require_uppercase: false,
  require_lowercase: false,
  require_number: false,
  require_symbol: false,
}

function getRequirements(policy: PasswordPolicy): Requirement[] {
  const requirements: Requirement[] = [
    {
      label: `At least ${policy.min_length} characters`,
      test: (value) => Array.from(value).length >= policy.min_length,
    },
  ]
  if (policy.require_uppercase) {
    requirements.push({
      label: 'At least one uppercase letter',
      test: (value) => /\p{Lu}/u.test(value),
    })
  }
  if (policy.require_lowercase) {
    requirements.push({
      label: 'At least one lowercase letter',
      test: (value) => /\p{Ll}/u.test(value),
    })
  }
  if (policy.require_number) {
    requirements.push({ label: 'At least one number', test: (value) => /\p{Nd}/u.test(value) })
  }
  if (policy.require_symbol) {
    requirements.push({
      label: 'At least one special character',
      test: (value) => /[^\p{L}\p{N}\s]/u.test(value),
    })
  }
  return requirements
}

type StrengthLevel = 'weak' | 'medium' | 'strong'

const STRENGTH_LABELS: Record<StrengthLevel, string> = {
  weak: 'Weak',
  medium: 'Medium',
  strong: 'Strong',
}

function getStrengthFromMetCount(
  metCount: number,
  total: number,
): { score: number; level: StrengthLevel | null } {
  if (total === 0 || metCount === 0) return { score: 0, level: null }
  const score = (metCount / total) * 100
  if (metCount < total) return { score, level: metCount / total <= 0.5 ? 'weak' : 'medium' }
  return { score, level: 'strong' }
}

export interface SetPasswordInputProps {
  userId?: string
  value: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  label?: string
  placeholder?: string
  helperText?: string
  errorText?: string
  id?: string
  fullWidth?: boolean
  required?: boolean
  showRequirements?: boolean
  showStrengthIndicator?: boolean
  onValidationChange?: (isValid: boolean) => void
  passwordType?: 'new' | 'confirm'
  className?: string
}

const SetPasswordInput = React.forwardRef<HTMLDivElement, SetPasswordInputProps>(
  (
    {
      userId,
      value,
      onChange,
      label = 'Password',
      placeholder,
      helperText,
      errorText,
      id,
      fullWidth = false,
      required = false,
      showRequirements = true,
      showStrengthIndicator = true,
      onValidationChange,
      passwordType = 'new',
      className,
    },
    ref,
  ) => {
    const [showPassword, setShowPassword] = useState(false)
    const generatedId = useId()
    const inputId = id ?? generatedId
    const messageId = `${inputId}-message`
    const [policy, setPolicy] = useState<PasswordPolicy | null>(null)

    useEffect((): (() => void) | undefined => {
      if (!userId || passwordType !== 'new') return undefined
      let cancelled = false
      setPolicy(null)
      apiZodios
        .core_getPasswordPolicy({ user_id: userId })
        .then((result) => {
          if (!cancelled) setPolicy(result)
        })
        .catch((error: unknown) => {
          console.error('Error fetching password policy', error)
          if (!cancelled) setPolicy(FALLBACK_POLICY)
        })
      return () => {
        cancelled = true
      }
    }, [userId, passwordType])

    const requirements = useMemo(
      () =>
        policy
          ? getRequirements(policy).map((req) => ({ label: req.label, met: req.test(value) }))
          : [],
      [policy, value],
    )

    const metCount = useMemo(() => requirements.filter((r) => r.met).length, [requirements])
    const { score, level: strengthLevel } = useMemo(
      () => getStrengthFromMetCount(metCount, requirements.length),
      [metCount, requirements.length],
    )

    const allMet = policy !== null && requirements.every((r) => r.met)

    useEffect(() => {
      onValidationChange?.(allMet)
    }, [allMet, onValidationChange])

    const handleTogglePassword = useCallback(() => setShowPassword((prev) => !prev), [])
    const handleMouseDown = useCallback((e: React.MouseEvent) => e.preventDefault(), [])

    const hasError = !!errorText
    const displayError = errorText

    const rootClass = [
      'internal-set-password-input',
      fullWidth && 'internal-set-password-input--full-width',
      className,
    ]
      .filter(Boolean)
      .join(' ')

    return (
      <div ref={ref} className={rootClass}>
        <TextField
          id={inputId}
          type={showPassword ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          label={label}
          placeholder={placeholder}
          error={hasError}
          required={required}
          containerStyle={{ width: '100%' }}
          inputProps={{
            'aria-label': `${passwordType} password ${showPassword ? 'visible' : 'hidden'}`,
            autoComplete: 'new-password',
            'aria-describedby': displayError || helperText ? messageId : undefined,
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <Tooltip title={showPassword ? 'Hide password' : 'Reveal password'}>
                  <IconButton
                    aria-label={`${showPassword ? 'hide' : 'show'} password`}
                    onClick={handleTogglePassword}
                    onMouseDown={handleMouseDown}
                    size='small'
                    color='default'
                  >
                    <Icon name={showPassword ? 'visibilityOff' : 'visibility'} fontSize='small' />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />
        <Typography
          id={messageId}
          aria-live='polite'
          color={hasError ? 'error' : 'textSecondary'}
          variant='body2'
          className='internal-set-password-input-message'
        >
          {displayError || helperText}
        </Typography>
        {(showStrengthIndicator || showRequirements) && (
          <div className='internal-set-password-input-strength-pattern'>
            {showStrengthIndicator && (
              <div className='internal-set-password-input-strength'>
                <div className='internal-set-password-input-strength-header'>
                  <Typography
                    variant='body2'
                    sx={{
                      color: 'text.primary',
                    }}
                  >
                    Password Strength
                  </Typography>
                  {strengthLevel && (
                    <Typography
                      className={`internal-set-password-input-strength-value--${strengthLevel}`}
                      variant='body2'
                    >
                      {STRENGTH_LABELS[strengthLevel]}
                    </Typography>
                  )}
                </div>
                <div className='internal-set-password-input-bar-track'>
                  {strengthLevel && (
                    <div
                      className={`internal-set-password-input-bar-fill internal-set-password-input-bar-fill--${strengthLevel}`}
                      style={{ width: `${score}%` }}
                    />
                  )}
                </div>
              </div>
            )}

            {showRequirements && (
              <ul className='internal-set-password-input-requirements'>
                {requirements.map((req) => (
                  <li key={req.label} className='internal-set-password-input-requirement'>
                    <span
                      className={`internal-set-password-input-req-icon internal-set-password-input-req-icon--${req.met ? 'met' : 'unmet'}`}
                    />
                    <Typography variant='body2' color={req.met ? 'success.main' : 'textSecondary'}>
                      {req.label}
                    </Typography>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    )
  },
)

SetPasswordInput.displayName = 'SetPasswordInput'

export default SetPasswordInput
